import { useMemo, useState } from "react";

import {
  Check,
  ExternalLink,
  Flag,
  Infinity as InfinityIcon,
  Info,
  Package,
  Trash2,
  Truck,
} from "lucide-react";
import { toast } from "sonner";

import { AlphabetIndex } from "@/components/cinch/AlphabetIndex";
import { BloodDripOverlay } from "@/components/cinch/BloodDrip";
import { CodeBlock } from "@/components/cinch/CodeBlock";
import { Combobox } from "@/components/cinch/Combobox";
import {
  DatePicker,
  DateRangePicker,
  MonthRangePicker,
  type DateRange,
  type MonthRange,
} from "@/components/cinch/DatePicker";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import {
  EventCalendar,
  shiftMonth,
  type EventCalendarEvent,
} from "@/components/cinch/EventCalendar";
import { IconButton } from "@/components/cinch/IconButton";
import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { OptionPicker } from "@/components/cinch/OptionPicker";
import { NumberInput } from "@/components/cinch/NumberInput";
import {
  ActiveFilterPill,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { Field } from "@/components/cinch/Field";
import { Logo } from "@/components/cinch/Logo";
import { MarkdownNotes } from "@/components/cinch/MarkdownNotes/MarkdownNotes";
import { Matrix, type MatrixCell } from "@/components/cinch/Matrix";
import { BenchmarkCell } from "@/components/cinch/BenchmarkCell";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EntityChain } from "@/components/cinch/EntityChain";
import { EntityName } from "@/components/cinch/EntityName";
import { RankDelta } from "@/components/cinch/RankDelta";
import { FlowGraph } from "@/components/cinch/FlowGraph";
import {
  RelationshipTree,
  type RelationshipTreeNode,
} from "@/components/cinch/RelationshipTree";
import { ReviewVerdict } from "@/components/cinch/ReviewVerdict";
import {
  EASTER_EGGS,
  SignInParticles,
  THEME_CLICK_HINTS,
  THEME_LABELS,
  getDateTheme,
  type ThemeName,
} from "@/components/cinch/SignInParticles";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { TierChip } from "@/components/cinch/TierChip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pagination } from "@/components/ui/pagination";

import { DEMO_BRAND, DEMO_PALETTE } from "./demoPalette";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { ColorDot, ColorDotStack } from "@/components/cinch/ColorDot";
import {
  AgChartPanel,
  AgGridPanel,
  buildAgOptions,
  makeValueFormatter,
  resolveToken,
  useIsDark,
  DEFAULT_PALETTE_ORDER,
  VALUE_FORMAT_LABEL,
  type ChartDataset,
  type ValueFormat,
} from "@/components/cinch/charts";
import { ScaleInput } from "@/components/cinch/ScaleInput";
import {
  SearchModal,
  highlightMatch,
  matchSnippet,
  type SearchModalItem,
} from "@/components/cinch/SearchModal";
import { EmptyState } from "@/components/cinch/EmptyState";
import { ImageLightbox } from "@/components/cinch/ImageLightbox";
import { KpiTile } from "@/components/cinch/KpiTile";
import { LinkOut } from "@/components/cinch/LinkOut";
import { ProductStatePill } from "@/components/cinch/ProductStatePill";
import { SectionDivider } from "@/components/cinch/SectionDivider";
import { SortableList } from "@/components/cinch/SortableList";
import { Sparkline } from "@/components/cinch/Sparkline";
import { StickyNote } from "@/components/cinch/StickyNote";

export function DesignSystemPage() {
  return (
    <div className="mx-auto max-w-6xl space-y-12 pb-16">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Design System</h1>
        <p className="text-sm text-muted-foreground">
          Tokens, primitives, and patterns used across cinch_ops. Reach for
          these first; if something's missing, add it here before duplicating
          across features.
        </p>
      </header>

      <BrandSection />
      <ColorTokensSection />
      <ChartDesignSection />
      <TypographySection />
      <ButtonsSection />
      <BadgesSection />
      <FormFieldSection />
      <NumberInputSection />
      <ComboboxSection />
      <DatePickerSection />
      <MarkdownNotesSection />
      <RichTextEditorSection />
      <CodeBlockSection />
      <AlphabetIndexSection />
      <ControlsSection />
      <TooltipSection />
      <TabsSection />
      <TableSection />
      <MatrixSection />
      <EventCalendarSection />
      <OptionPickerSection />
      <CollapsibleCardSection />
      <BulkActionBarSection />
      <IconButtonSection />
      <DataTableSection />
      <EntityNameSection />
      <EntityChainSection />
      <PaginationSection />
      <FilterBarSection />
      <FeedbackSection />
      <DrawerSection />
      <ConfirmDialogSection />
      <RelationshipTreeSection />
      <FlowGraphSection />
      <ReviewVerdictSection />
      <SortableListSection />
      <KpiTileSection />
      <ProductStatePillSection />
      <SectionDividerSection />
      <EmptyStateSection />
      <ImageLightboxSection />
      <LinkOutSection />
      <SparklineSection />
      <StickyNoteSection />
      <ColorDotSection />
      <ScaleInputSection />
      <SearchModalSection />
      <BloodDripSection />
      <SignInParticlesSection />
    </div>
  );
}

function EventCalendarSection() {
  const [{ year, month }, setMonth] = useState({ year: 2026, month: 8 });
  const [picked, setPicked] = useState<string | null>(null);

  // An event can belong to TWO days — the day it happens and the day its
  // effect lands. Payroll accrues at period end and clears days later; a hire
  // starts mid-month and is first paid by the next run.
  const events: EventCalendarEvent[] = [
    {
      id: "pay",
      title: "Payroll · End-month",
      date: "2026-08-02",
      linkedDate: "2026-07-31",
      trailing: "-$199K",
      chipClassName: "bg-brand/10 text-brand border-brand/30",
      tooltip: "Payroll for the period ending 31 Jul",
    },
    {
      id: "hire",
      title: "N. Okafor",
      date: "2026-08-16",
      linkedDate: "2026-08-12",
      trailing: "-$12K",
      chipClassName: "bg-power/10 text-power border-power/30",
      tooltip: "New hire — first paid by the run ending 15 Aug",
    },
    {
      id: "vendor",
      title: "Twilio",
      date: "2026-08-09",
      trailing: "-$54K",
      chipClassName: "bg-warning-light text-warning border-warning/30",
    },
    {
      id: "churn",
      title: "LaJaunie's",
      date: "2026-08-30",
      linkedDate: "2026-08-20",
      trailing: "-$2.2K",
      chipClassName: "bg-danger/10 text-danger border-danger/30 opacity-60 border-dashed",
      tooltip: "Speculative churn — dashed + faded, and the engine ignores it",
    },
    {
      id: "revenue",
      title: "Heartland",
      date: "2026-08-23",
      trailing: "+$7.1K",
      chipClassName: "bg-success-light text-success border-success/30",
    },
  ];

  return (
    <Section
      title="EventCalendar"
      description="A month calendar of dated events — distinct from DatePicker, which picks a date. Its one opinion: an event can belong to TWO days, the day it happens and the day its effect lands, drawn as a solid chip on `date` and a faint arrowed chip on `linkedDate`. That makes the gap between cause and effect visible instead of implied — a hire starting on the 12th costs nothing on the 12th. Tone is the caller's (pass `chipClassName` per event); the component owns layout, the two-date rule, and click/keyboard behaviour. Built for the Financial Forecast Calendar. Pass `onSelectDay` to make empty space clickable, omit it for a read-only board."
    >
      <EventCalendar
        year={year}
        month={month}
        events={events}
        onPrev={() => setMonth(shiftMonth(year, month, -1))}
        onNext={() => setMonth(shiftMonth(year, month, 1))}
        onToday={() => setMonth({ year: 2026, month: 8 })}
        onSelectEvent={(e) => setPicked(`event: ${e.title}`)}
        onSelectDay={(iso) => setPicked(`day: ${iso}`)}
        footnote={
          <>
            Faint + arrow = the day it happens. Solid = the day money moves.{" "}
            {picked && <strong>Clicked {picked}</strong>}
          </>
        }
      />
    </Section>
  );
}

function OptionPickerSection() {
  const [state, setState] = useState("speculative");
  const [tone, setTone] = useState("expected");
  return (
    <Section
      title="OptionPicker"
      description="A small set of mutually exclusive choices as pills, with a line explaining what the SELECTED one means. Built for choices where the labels aren't self-explanatory and picking wrong has consequences — forecast planning states, where three of four options behave identically and only one ('baked in') changes the arithmetic. A Select hides that; a radio group leaves the caller to find somewhere for the explanation. Reach for a Select instead past ~5 options, or when the labels already say everything."
    >
      <div className="flex max-w-lg flex-col gap-6">
        <div>
          <p className="mb-1.5 text-xs font-medium">Headcount planning state</p>
          <OptionPicker
            label="Headcount planning state"
            value={state}
            onChange={setState}
            options={[
              { value: "speculative", label: "Speculative", description: "We might hire this. A plan, not a person." },
              { value: "offer_accepted", label: "Offer accepted", description: "Offer out and accepted; not yet on payroll." },
              { value: "started", label: "Started", description: "They're here; the pay calendar hasn't caught up." },
              { value: "baked_in", label: "Baked in", description: "Already inside the pay-run amounts. Adds NO extra cost." },
            ]}
          />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Tones + no description</p>
          <OptionPicker
            tone="warning"
            value={tone}
            onChange={setTone}
            hideDescription
            options={[
              { value: "speculative", label: "Speculative" },
              { value: "expected", label: "Expected" },
              { value: "committed", label: "Committed" },
            ]}
          />
        </div>
      </div>
    </Section>
  );
}

function CollapsibleCardSection() {
  const [open, setOpen] = useState(false);
  return (
    <Section
      title="CollapsibleCard"
      description="A titled block that collapses to a single clickable row. For reference content that earns its place but shouldn't cost a screenful every visit — the reconciliation steps' 'What this step does' panel is the case that prompted it: useful the first few times you work a step, pure scroll tax by the tenth, and expanded it pushed the worklist below the fold on all 21 steps. The chevron rotates rather than swapping icons, so it reads as one control in two states. Content is unmounted when closed, not hidden. Use a plain Card when the content IS the point of the page; use this when it's context for the point."
    >
      <div className="flex max-w-2xl flex-col gap-6">
        <div>
          <p className="mb-1.5 text-xs font-medium">Collapsed by default, with meta</p>
          <CollapsibleCard
            title="What this step does"
            meta="Make sure the integration fees we pay are for customers who are still paying us."
          >
            <p className="text-sm">
              Reconciles what each vendor billed against the rooftops that
              actually sent data in the invoice's service period.
            </p>
          </CollapsibleCard>
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Open by default</p>
          <CollapsibleCard title="Assumptions" defaultOpen>
            <p className="text-sm text-muted-foreground">
              Expanded on first render for content people usually want.
            </p>
          </CollapsibleCard>
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">
            Controlled + bare (no Card chrome, for nesting in an existing panel)
          </p>
          <div className="rounded-md border">
            <CollapsibleCard
              bare
              title="Advanced options"
              open={open}
              onOpenChange={setOpen}
              meta={open ? "showing" : "hidden"}
            >
              <p className="text-sm text-muted-foreground">
                Parent owns the state — pair `open` with `onOpenChange`.
              </p>
            </CollapsibleCard>
          </div>
        </div>
      </div>
    </Section>
  );
}

function BulkActionBarSection() {
  const [log, setLog] = useState("nothing yet");
  return (
    <Section
      title="BulkActionBar"
      description="Applies an action to every row a table is CURRENTLY SHOWING. Built for filtered worklists where the useful move is 'everything I can see right now' — filter the integration-expense reconciliation to 'no data', confirm all six — rather than ticking a checkbox per row. Two deliberate safeguards, because bulk actions over a filter are easy to get wrong: the count lives in the button ('Approve 6 lines', never a bare 'Approve all', which leaves you wondering whether the hidden rows moved too), and the first click swaps the bar for an inline Confirm/Cancel that restates the scope and how many rows will NOT change. Pluralisation defaults to a trailing 's'; pass `nounPlural` for the nouns that breaks ('company' → 'companys'). Use per-row controls when the rows need different answers; use this when the filter has already made them the same answer."
    >
      <div className="flex max-w-3xl flex-col gap-4">
        <div>
          <p className="mb-1.5 text-xs font-medium">Filtered view — some rows hidden</p>
          <BulkActionBar
            count={6}
            totalCount={187}
            noun="line"
            actions={[
              { label: "Approve", tone: "success", onClick: () => setLog("approved 6") },
              { label: "Flag", tone: "warning", onClick: () => setLog("flagged 6") },
            ]}
          />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Nothing hidden</p>
          <BulkActionBar
            count={12}
            totalCount={12}
            noun="account"
            actions={[{ label: "Settle", onClick: () => setLog("settled 12") }]}
          />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium">Irregular plural — `nounPlural`</p>
          <BulkActionBar
            count={312}
            totalCount={406}
            noun="company"
            nounPlural="companies"
            actions={[
              { label: "Verify", tone: "success", onClick: () => setLog("verified 312") },
            ]}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Renders nothing at all when the count is 0. Last action: <strong>{log}</strong>
        </p>
      </div>
    </Section>
  );
}

function IconButtonSection() {
  const [verdict, setVerdict] = useState<"none" | "approved" | "flagged">("none");
  return (
    <Section
      title="IconButton"
      description="A bare icon-only action sized to sit inline in a dense table row. Deliberately not <Button variant='ghost' size='icon'> — that carries padding and a focus ring meant for standalone controls, which is too heavy when three share a cell. It stops click propagation, so it's safe inside a clickable row. `title` is required and doubles as the accessible name; `active` sets aria-pressed and applies `activeClassName`. This is the reconciliation row-action idiom (approve / flag / settle), previously copy-pasted per panel."
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2">
          <span className="mr-2 text-sm text-muted-foreground">Row actions</span>
          <IconButton
            title="Approve — expected, no action needed"
            active={verdict === "approved"}
            activeClassName="text-success"
            hoverClassName="hover:bg-success-light hover:text-success"
            onClick={() => setVerdict(verdict === "approved" ? "none" : "approved")}
          >
            <Check className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            title="Flag — needs chasing"
            active={verdict === "flagged"}
            activeClassName="text-warning"
            hoverClassName="hover:bg-warning-light hover:text-warning"
            onClick={() => setVerdict(verdict === "flagged" ? "none" : "flagged")}
          >
            <Flag className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton title="Disabled example" disabled onClick={() => {}}>
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </span>
        <span className="text-xs text-muted-foreground">
          verdict: <strong>{verdict}</strong>
        </span>
      </div>
    </Section>
  );
}

function BloodDripSection() {
  const [bleeding, setBleeding] = useState(false);
  return (
    <Section
      title="BloodDripOverlay"
      description="Full-screen theatrical penalty: blood pools at the top of the viewport and drips to the bottom (~5s), blocks input while running, then fades out and fires onComplete. Used by Proposals when saving a sub-30% ('Absolutely Not') margin — the save still goes through afterward. Skips straight to onComplete under prefers-reduced-motion. Use extremely sparingly; this is a consequence, not a loading state."
    >
      <Button variant="destructive" onClick={() => setBleeding(true)} disabled={bleeding}>
        {bleeding ? "Bleeding…" : "Trigger the blood drip"}
      </Button>
      <BloodDripOverlay active={bleeding} onComplete={() => setBleeding(false)} />
    </Section>
  );
}

function SignInParticlesSection() {
  const todaysTheme = getDateTheme();
  const [picked, setPicked] = useState<ThemeName>(todaysTheme);
  // Reverse the egg table so the preview can show the code for each theme.
  const codeFor = useMemo(() => {
    const map = new Map<ThemeName, string>();
    for (const [code, name] of Object.entries(EASTER_EGGS)) if (!map.has(name)) map.set(name, code);
    return map;
  }, []);
  const themes = Object.keys(THEME_LABELS) as ThemeName[];

  return (
    <Section
      title="SignInParticles"
      description="The animated backdrop on the sign-in screen — a mouse-reactive dot plexus that connects nearby dots with lines, orbits the cursor, and blows apart when you click the backdrop. It swaps itself for a seasonal theme on the right dates (New Year, Valentine's, St. Patrick's, spring rain, Cinch launch day, May the Fourth, July 4th, Halloween, Christmas) and carries keyboard easter eggs on top of that: type a code anywhere on the sign-in screen, outside a text field, and the field restarts in that theme. Ported from the CinchAI login page so both products behave identically. Static under prefers-reduced-motion. Sign-in only — do not decorate app pages with it."
    >
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Today ({new Date().toLocaleDateString()}) the sign-in screen runs{" "}
          <strong>{THEME_LABELS[todaysTheme]}</strong>.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {themes.map((t) => (
            <Button
              key={t}
              size="sm"
              variant={t === picked ? "default" : "outline"}
              onClick={() => setPicked(t)}
            >
              {THEME_LABELS[t]}
            </Button>
          ))}
        </div>
        <div className="relative h-80 overflow-hidden rounded-lg border bg-background">
          <SignInParticles key={picked} theme={picked} contained />
          <div className="relative z-10 flex h-full items-center justify-center">
            <div className="rounded-xl border bg-card/80 px-6 py-4 text-center shadow-sm backdrop-blur-sm">
              <p className="text-sm font-semibold">{THEME_LABELS[picked]}</p>
              <p className="text-xs text-muted-foreground">
                {codeFor.has(picked) ? (
                  <>
                    type <code>{codeFor.get(picked)}</code>
                  </>
                ) : (
                  "no egg code — date-driven only"
                )}
              </p>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {THEME_CLICK_HINTS[picked] ?? "Click does nothing in this theme — it's meant to be calm."}{" "}
          Click inside the box above to try it.
        </p>
      </div>
    </Section>
  );
}

function SearchModalSection() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const corpus = [
    { icon: Info, title: "Automated Billing", text: "Ship automated billing to stop manual invoice fixes." },
    { icon: Info, title: "AI Voice Agent", text: "Voice agent with pop-up customer drawer." },
    { icon: Info, title: "Support Capabilities", text: "Fast response times and high team retention." },
  ];
  const q = query.trim().toLowerCase();
  const items: SearchModalItem[] = !q
    ? []
    : corpus
        .filter((c) => c.title.toLowerCase().includes(q) || c.text.toLowerCase().includes(q))
        .map((c, i) => ({
          id: String(i),
          icon: c.icon,
          title: highlightMatch(c.title, query),
          snippet: highlightMatch(matchSnippet(c.text, query), query),
          hint: "Demo",
          onSelect: () => setOpen(false),
        }));
  return (
    <Section
      title="Search modal"
      description="Command-palette style search: borderless input on top, keyboard-navigable results below (↑/↓ + Enter). The caller owns query state and passes already-filtered items — each with a section icon, title, snippet, and hint. Pair with highlightMatch/matchSnippet so the hit is visible. Used by Exec Planning's session search."
    >
      <Button onClick={() => setOpen(true)}>Open search modal</Button>
      <SearchModal
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
        query={query}
        onQueryChange={setQuery}
        items={items}
        placeholder="Try 'billing'…"
      />
    </Section>
  );
}

const FORMAT_SAMPLE: Record<ValueFormat, number> = {
  usd: 1234567.89,
  usd_compact: 1234567.89,
  pct: 87.5,
  count: 1234567,
  plain: 1234.5,
};

function ChartDesignSection() {
  // resolveToken reads the live cascade; re-resolve colors on a theme flip.
  const isDark = useIsDark();
  const lineDs: ChartDataset = useMemo(
    () => ({
      key: "demo-arr",
      label: "ARR",
      kind: "timeseries",
      chartTypes: ["line"],
      categoryKey: "month",
      rows: [
        { month: "Jan", arr: 820_000 },
        { month: "Feb", arr: 861_000 },
        { month: "Mar", arr: 905_000 },
        { month: "Apr", arr: 892_000 },
        { month: "May", arr: 951_000 },
        { month: "Jun", arr: 1_010_000 },
      ],
      series: [{ key: "arr", name: "ARR" }],
      colors: [resolveToken("brand")],
      valueFormat: makeValueFormatter("usd_compact"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDark],
  );
  const donutDs: ChartDataset = useMemo(
    () => ({
      key: "demo-mix",
      label: "Segment mix",
      kind: "category",
      chartTypes: ["donut"],
      categoryKey: "segment",
      rows: [
        { segment: "SMB", value: 48 },
        { segment: "Mid-market", value: 34 },
        { segment: "Enterprise", value: 18 },
      ],
      series: [{ key: "value", name: "Accounts" }],
      colors: [resolveToken("brand"), resolveToken("teal"), resolveToken("purple")],
      valueFormat: makeValueFormatter("count"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDark],
  );
  // Combination: bars + a line on one chart (per-series `type` override).
  const comboDs: ChartDataset = useMemo(
    () => ({
      key: "demo-combo",
      label: "Bookings vs target",
      kind: "category",
      chartTypes: ["bar"],
      categoryKey: "month",
      rows: [
        { month: "Jan", bookings: 42, target: 40 },
        { month: "Feb", bookings: 55, target: 45 },
        { month: "Mar", bookings: 48, target: 50 },
        { month: "Apr", bookings: 67, target: 55 },
        { month: "May", bookings: 61, target: 60 },
        { month: "Jun", bookings: 78, target: 65 },
      ],
      series: [
        { key: "bookings", name: "Bookings" },
        { key: "target", name: "Target", type: "line" },
      ],
      colors: [resolveToken("brand"), resolveToken("orange")],
      valueFormat: makeValueFormatter("count"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDark],
  );
  const gridRows = [
    { account: "Sage Pest Control", segment: "SMB", mrr: 1845, status: "Active" },
    { account: "Magpie Barker Ent.", segment: "Mid-market", mrr: 7320, status: "Active" },
    { account: "Tire Discounters", segment: "Enterprise", mrr: 21400, status: "Renewing" },
    { account: "Moxie HVAC", segment: "SMB", mrr: 990, status: "At risk" },
  ];

  return (
    <Section
      title="Chart design — AG Charts & AG Grid"
      description="The brand styling we apply to AG visuals, and the shared components that carry it. Import { AgChartPanel, AgGridPanel, buildAgOptions, makeValueFormatter } from @/components/cinch/charts — never wire ag-charts/ag-grid straight into a feature. buildAgOptions covers the full free AG Charts Community set — line, area, stacked area, bar, horizontal bar, stacked bar, scatter, bubble, pie, donut — plus computed trend lines (linear / moving-average / reference) and per-series combination (bars + a line). Everything below re-themes automatically when you toggle dark mode (canvas colors are resolved from DS tokens on the fly)."
    >
      <div className="space-y-6">
        <div>
          <SubLabel>Series palette (default order)</SubLabel>
          <div className="flex flex-wrap gap-4">
            {DEFAULT_PALETTE_ORDER.map((c, i) => (
              <div key={c} className="flex items-center gap-2">
                <ColorDot color={resolveToken(c)} size="sm" />
                <span className="text-xs text-muted-foreground">
                  {i + 1}. {c}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Multi-series charts consume this order unless the dataset assigns its own
            colors. Brand cyan leads; keep semantic colors (success/warning/danger)
            for status, not arbitrary series.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <SubLabel>AgChartPanel — line</SubLabel>
            <AgChartPanel options={buildAgOptions(lineDs, "line", isDark)} className="h-64" />
          </div>
          <div>
            <SubLabel>AgChartPanel — donut</SubLabel>
            <AgChartPanel options={buildAgOptions(donutDs, "donut", isDark)} className="h-64" />
          </div>
          <div>
            <SubLabel>Combination — bars + a line (per-series type)</SubLabel>
            <AgChartPanel options={buildAgOptions(comboDs, "bar", isDark)} className="h-64" />
          </div>
          <div>
            <SubLabel>Trend line — linear regression overlay</SubLabel>
            <AgChartPanel
              options={buildAgOptions({ ...lineDs, trend: { type: "linear" } }, "line", isDark)}
              className="h-64"
            />
          </div>
        </div>

        <div>
          <SubLabel>AgGridPanel</SubLabel>
          <AgGridPanel
            className="h-56"
            columns={[
              { field: "account", headerName: "Account", flex: 2 },
              { field: "segment", headerName: "Segment" },
              {
                field: "mrr",
                headerName: "MRR",
                type: "rightAligned",
                valueFormatter: (p) => makeValueFormatter("usd")(Number(p.value)),
              },
              { field: "status", headerName: "Status" },
            ]}
            rows={gridRows}
          />
        </div>

        <div>
          <SubLabel>Value formats</SubLabel>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {(Object.keys(VALUE_FORMAT_LABEL) as ValueFormat[]).map((k) => (
              <div key={k} className="text-sm">
                <code className="text-xs text-muted-foreground">{k}</code>
                <span className="mx-2 text-muted-foreground">→</span>
                <span className="font-medium tabular-nums">
                  {makeValueFormatter(k)(FORMAT_SAMPLE[k])}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 border-t border-border pt-4 text-sm">
          <a
            href="https://www.ag-grid.com/charts/react/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand hover:underline"
          >
            AG Charts docs <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a
            href="https://www.ag-grid.com/react-data-grid/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand hover:underline"
          >
            AG Grid docs <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </Section>
  );
}

function ColorDotSection() {
  const brand = DEMO_BRAND;
  return (
    <Section
      title="Color dot & stack"
      description="Identity color swatch for a person/entity — participant dots on cards, the author dot on a sticky note, color-picker swatches. ColorDotStack overlaps a row and collapses the overflow into a +N. Presentational; wrap in a button to make a swatch clickable."
    >
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <ColorDot color={brand} size="xs" />
          <ColorDot color={brand} size="sm" />
          <ColorDot color={brand} size="md" />
          <ColorDot color={brand} size="md" selected />
        </div>
        <ColorDotStack
          items={DEMO_PALETTE.map((c, i) => ({ color: c, title: `Member ${i + 1}` }))}
          max={4}
          size="md"
        />
      </div>
    </Section>
  );
}

function ScaleInputSection() {
  const [impact, setImpact] = useState<number | null>(8);
  const [effort, setEffort] = useState<number | null>(3);
  return (
    <Section
      title="Scale input"
      description="Discrete 1–N rating scale — a row of numbered buttons with one active. For planning-poker scoring, effort/impact votes, confidence ratings: any bounded whole-number pick where seeing the whole range matters (unlike NumberInput's stepper). Optional fixed-width label so stacked scales line up."
    >
      <div className="max-w-md space-y-2">
        <ScaleInput label="Impact" value={impact} onChange={setImpact} />
        <ScaleInput label="Effort" value={effort} onChange={setEffort} />
      </div>
    </Section>
  );
}

function StickyNoteSection() {
  return (
    <Section
      title="Sticky note"
      description="Sticky-note card for canvas/workshop surfaces (Exec Planning's SWOT walls and Impact vs Effort grid). Tone carries meaning — board color or impact-category legend — so pick it from the domain. Purely presentational: drag handlers, menus, and dialogs belong to the caller."
    >
      <div className="flex flex-wrap items-start gap-3">
        <StickyNote
          tone="green"
          title="Deep vertical expertise"
          description="We know the towing space better than any competitor."
          footer="Justin Rae"
        />
        <StickyNote
          tone="blue"
          title="Loyalty v2"
          description="Retention play for the existing base."
          footer="Jared Rich"
          size="sm"
        />
        <StickyNote
          tone="orange"
          title="OEM recommendations"
          footer="Jared Rich"
          size="sm"
          selected
        />
        <StickyNote tone="gray" title="Scheduler" size="sm" />
        <StickyNote tone="red" title="New entrant undercutting price" size="sm" />
        <StickyNote tone="yellow" title="Default tone" size="sm" />
      </div>
    </Section>
  );
}

function ConfirmDialogSection() {
  const [open, setOpen] = useState(false);
  return (
    <Section
      title="Confirm dialog"
      description="Use Dialog + DialogHeader / DialogTitle / DialogDescription / DialogFooter to build a confirm modal. Reach for this whenever an action is irreversible (lock a week, delete a record, force-push, etc.) — the modal gives the user one beat to back out."
    >
      <Button onClick={() => setOpen(true)}>Open confirm dialog</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lock Wk 25 2026?</DialogTitle>
            <DialogDescription>
              Locking commits this week's snapshot as the historical record.
              All notes and calls for the week become read-only and only
              an admin can unlock.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setOpen(false)}>Lock week</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function SparklineSection() {
  const trendUp = [4, 6, 5, 8, 7, 9, 12, 11, 14, 17];
  const trendDown = [20, 18, 17, 19, 14, 13, 11, 9, 8, 6];
  const flat = [10, 11, 10, 12, 11, 10, 11, 12, 10, 11];
  return (
    <Section
      title="Sparkline"
      description="Minimal inline SVG trend chart for embedding inside KPI tiles. Auto-scales; optional baseline + end dot."
    >
      <div className="grid grid-cols-3 gap-4 max-w-xl">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Positive trend
          </div>
          <Sparkline points={trendUp} tone="positive" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Danger trend
          </div>
          <Sparkline points={trendDown} tone="danger" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            With baseline
          </div>
          <Sparkline points={flat} tone="brand" baseline={12} />
        </div>
      </div>
    </Section>
  );
}

function AlphabetIndexSection() {
  const [active, setActive] = useState<string | null>("C");
  // A few letters "have entries"; the rest render dimmed + non-interactive.
  const available = ["#", "A", "C", "E", "G", "M", "N", "P", "R", "S", "T"];
  return (
    <Section
      title="AlphabetIndex"
      description="Contacts-style A–Z jump rail for long alphabetical lists. Letters with no entries dim out; clicking one calls onSelect (the caller scrolls the row into view) and the in-view letter stays highlighted. Sticky-position it beside a scrolling list — the Glossary uses it to jump between terms. Pair with the exported letterOf() helper to bucket each row."
    >
      <div className="flex items-start gap-6">
        <AlphabetIndex
          available={available}
          active={active}
          onSelect={setActive}
          className="rounded-md border bg-card p-1"
        />
        <p className="text-sm text-muted-foreground">
          Selected letter: <code className="rounded bg-muted px-1">{active ?? "—"}</code>
        </p>
      </div>
    </Section>
  );
}

function CodeBlockSection() {
  return (
    <Section
      title="CodeBlock"
      description="Monospace formula / query block with an optional eyebrow label and a copy button (hover to reveal). Scrolls horizontally instead of wrapping, so long lines never break the layout. tone='brand' (cyan rail) for equations; neutral for SQL / snippets. Used on every Glossary term to show the equation and query behind a metric."
    >
      <div className="max-w-2xl space-y-3">
        <CodeBlock label="Equation" tone="brand" code="NRR % = Σ current_arr / Σ prior_year_arr × 100" />
        <CodeBlock
          label="Query"
          code={"SELECT counterparty_id, SUM(amount_current) AS mrr\nFROM cinch_marts.bi.revenue_waterfall\nWHERE is_mrr_subscription = 1\nGROUP BY 1"}
        />
        <CodeBlock code="baseline_monthly = MAX(prior_month_billed_mrr, replaced_mrr)" copyable={false} />
      </div>
    </Section>
  );
}

function NumberInputSection() {
  const [qty, setQty] = useState<number | null>(815000);
  const [price, setPrice] = useState<number | null>(0.028);
  const [plain, setPlain] = useState<number | null>(null);
  return (
    <Section
      title="NumberInput"
      description="Formatted numeric input — thousands separators (and optional currency prefix) when idle, raw editable number while focused. Emits number | null, never a string. Use for any money / large-count field (proposal qty & unit price, budgets) so 815,000 stays readable but typing stays sane."
    >
      <div className="grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <SubLabel>Count (qty)</SubLabel>
          <NumberInput value={qty} onChange={setQty} maxDecimals={0} align="right" />
          <p className="mt-1 text-xs text-muted-foreground">value: {String(qty)}</p>
        </div>
        <div>
          <SubLabel>Currency ($)</SubLabel>
          <NumberInput value={price} onChange={setPrice} prefix="$" maxDecimals={4} />
          <p className="mt-1 text-xs text-muted-foreground">value: {String(price)}</p>
        </div>
        <div>
          <SubLabel>Empty / placeholder</SubLabel>
          <NumberInput value={plain} onChange={setPlain} placeholder="0" />
          <p className="mt-1 text-xs text-muted-foreground">value: {String(plain)}</p>
        </div>
      </div>
    </Section>
  );
}

function ComboboxSection() {
  const [value, setValue] = useState<string | null>(null);
  const [remote, setRemote] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const options = [
    { id: "it_1", label: "Customer Records", subtitle: "Subscription", group: "Items", keywords: "records" },
    { id: "it_2", label: "Text Message Credits", subtitle: "Usage", group: "Items", keywords: "sms texts" },
    { id: "it_3", label: "Surveys", subtitle: "Usage", group: "Items" },
    { id: "pk_1", label: "BETA Cinch AI — Small", subtitle: "Package", group: "Packages", badge: "Flat" },
    { id: "pk_2", label: "Growth Bundle", subtitle: "Package", group: "Packages", badge: "Per Unit" },
  ];
  // Stands in for a server search: options appear only once something is typed,
  // the way the Cinch company picker behaves.
  const remoteOptions = query.trim().length >= 2
    ? [
        { id: "co_1", label: `${query} Automotive`, subtitle: "Cinch company" },
        { id: "co_2", label: `${query} Pest Control`, subtitle: "Cinch company" },
      ]
    : [];
  return (
    <Section
      title="Combobox"
      description="Searchable single-select with optional grouping + a right-aligned badge per option — the type-ahead alternative to a plain Select when the list is long (e.g. picking from the full Cinch product catalog). Emits the option id, or null when cleared. Pass onSearchChange (+ loading) when the candidates come from a SERVER rather than a preloaded list: the callback fires as the user types, the caller refetches, and the local filter still runs over whatever came back."
    >
      <div className="flex flex-wrap gap-8">
        <div className="max-w-sm space-y-2">
          <p className="text-xs font-medium">Local list</p>
          <Combobox
            options={options}
            value={value}
            onChange={setValue}
            placeholder="Select product…"
            searchPlaceholder="Search items & packages…"
          />
          <p className="text-xs text-muted-foreground">selected: {value ?? "—"}</p>
        </div>
        <div className="max-w-sm space-y-2">
          <p className="text-xs font-medium">Server-driven (onSearchChange)</p>
          <Combobox
            options={remoteOptions}
            value={remote}
            onChange={setRemote}
            onSearchChange={setQuery}
            placeholder="Search companies…"
            searchPlaceholder="Type at least 2 letters…"
            emptyText="Type to search."
          />
          <p className="text-xs text-muted-foreground">query: {query || "—"}</p>
        </div>
      </div>
    </Section>
  );
}

function DatePickerSection() {
  const [single, setSingle] = useState<Date | null>(null);
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [openEnded, setOpenEnded] = useState<DateRange>({ start: null, end: null });
  const [bounded, setBounded] = useState<Date | null>(null);
  const [monthRange, setMonthRange] = useState<MonthRange>({
    start: "2026-01",
    end: "2026-08",
  });

  const fmt = (d: Date | null) => (d ? d.toLocaleDateString() : "null");
  const fmtRange = (r: DateRange) => `${fmt(r.start)} → ${fmt(r.end)}`;

  // Fixed window so the constrained example is deterministic regardless of today.
  const minDate = new Date(2026, 0, 1);
  const maxDate = new Date(2026, 11, 31);

  return (
    <Section
      title="Date picker"
      description="The one unified date control across cinch_ops — a text input that accepts many typed formats (10/1/2025, 2025-10-01, '1 Jan 2026', see parseFlexDate) with a drill-down calendar popover (month → year → decade). Single and range variants share one look; brand colors come from CSS vars, no hex literals. Emits Date | null (or a { start, end } DateRange), never a string. Reach for this instead of a native date input so keyboard entry, formatting, and theming stay consistent."
    >
      <div className="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <SubLabel>Single date</SubLabel>
          <DatePicker value={single} onChange={setSingle} placeholder="Pick a date" />
          <p className="mt-1 text-xs text-muted-foreground">value: {fmt(single)}</p>
        </div>

        <div>
          <SubLabel>Constrained (2026 only)</SubLabel>
          <DatePicker
            value={bounded}
            onChange={setBounded}
            minDate={minDate}
            maxDate={maxDate}
            placeholder="Within 2026"
          />
          <p className="mt-1 text-xs text-muted-foreground">value: {fmt(bounded)}</p>
        </div>

        <div>
          <SubLabel>Date range</SubLabel>
          <DateRangePicker value={range} onChange={setRange} placeholder="Pick a range" />
          <p className="mt-1 text-xs text-muted-foreground">value: {fmtRange(range)}</p>
        </div>

        <div>
          <SubLabel>Open-ended range</SubLabel>
          <DateRangePicker
            value={openEnded}
            onChange={setOpenEnded}
            allowOpenEnded
            placeholder="From / until"
          />
          <p className="mt-1 text-xs text-muted-foreground">value: {fmtRange(openEnded)}</p>
        </div>

        <div>
          <SubLabel>Month range</SubLabel>
          <MonthRangePicker value={monthRange} onChange={setMonthRange} />
          <p className="mt-1 text-xs text-muted-foreground">
            value: {monthRange.start ?? "…"} → {monthRange.end ?? "…"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            For accounting periods, where a month is the unit. Asking someone to
            pick Aug 1 – Aug 31 on a day calendar makes them do arithmetic to
            express something they already knew — and invites the off-by-one
            where the last day quietly leaves the books.
          </p>
        </div>
      </div>
    </Section>
  );
}

function EmptyStateSection() {
  return (
    <Section
      title="Empty state"
      description="“There’s nothing here” for card grids and panels. DataTable already owns the empty state inside a table (emptyMessage) — this is the one for everything else. Say what would be here and how it gets here: “No shipments recorded” leaves the reader wondering whether it’s broken; adding “recording a box puts it here” tells them nothing is wrong and what to do."
    >
      <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
        <div>
          <SubLabel>With icon and guidance</SubLabel>
          <EmptyState icon={<Truck />} title="No shipments recorded">
            Anything given away that needs mailing shows in the send queue;
            recording a box puts it here.
          </EmptyState>
        </div>
        <div>
          <SubLabel>With an action</SubLabel>
          <EmptyState
            icon={<Package />}
            title="No swag loaded yet"
            action={
              <Button size="sm" variant="outline">
                Import the spreadsheet
              </Button>
            }
          >
            An admin can add purchases, or import the old workbook in one go.
          </EmptyState>
        </div>
        <div className="sm:col-span-2">
          <SubLabel>Bare (already inside a bordered box)</SubLabel>
          <div className="rounded-md border">
            <EmptyState bare title="Nothing matches those filters">
              Try clearing one.
            </EmptyState>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ImageLightboxSection() {
  const [open, setOpen] = useState(false);
  // A tiny inline SVG stands in for a real photo so the section needs no asset.
  // The fill is read from the --cinch-brand token rather than written as a hex,
  // so it tracks the theme (and doesn't trip the no-hex-in-tsx rule).
  const demo = useMemo(() => {
    const hsl =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--cinch-brand")
        .trim() || "201 90% 49%";
    return (
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="400">
           <rect width="640" height="400" fill="hsl(${hsl})"/>
           <text x="320" y="210" font-family="Inter,sans-serif" font-size="34"
                 fill="white" text-anchor="middle">Minky Blanket</text>
         </svg>`,
      )
    );
  }, []);

  return (
    <Section
      title="Image lightbox"
      description="Full-screen view of one image over a dimmed backdrop, for when a thumbnail is too small to answer the question it exists to answer — which blanket is that, what does the order photo actually show. Dismisses on backdrop click, Escape, or the close button; it announces as a dialog, moves focus to close and returns it to the thumbnail on dismiss, and locks body scroll while open. Render it conditionally rather than passing an `open` prop — there is nothing worth keeping mounted between viewings."
    >
      <div className="flex max-w-md flex-col gap-2">
        <SubLabel>Click the thumbnail</SubLabel>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative block h-36 w-64 cursor-zoom-in overflow-hidden rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="View Minky Blanket larger"
        >
          <img
            src={demo}
            alt="Minky Blanket"
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          />
        </button>
        {open && (
          <ImageLightbox
            url={demo}
            alt="Minky Blanket"
            caption="Minky Blanket — 3 on hand, $100.00 each"
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </Section>
  );
}

function LinkOutSection() {
  return (
    <Section
      title="Link out"
      description="A link that leaves the app — a carrier's tracking page, a HubSpot deal, a QuickBooks report. Always carries the outbound arrow and opens in a new tab. The arrow is the point: it sets the expectation that the click leaves CinchOps, which matters most in a table where every other link navigates in place. With a null href it degrades to plain muted text, so a row with nothing to link to needs no conditional at the call site."
    >
      <div className="flex max-w-2xl flex-col gap-3">
        <div>
          <SubLabel>Default</SubLabel>
          <LinkOut href="https://www.fedex.com/fedextrack/?trknbr=888915283960">
            Track on FedEx
          </LinkOut>
        </div>
        <div>
          <SubLabel>Dense (xs, for table cells)</SubLabel>
          <LinkOut href="https://tools.usps.com/go/TrackConfirmAction" size="xs">
            9505 5150 3239 5184 2505 03
          </LinkOut>
        </div>
        <div>
          <SubLabel>No href — degrades to text</SubLabel>
          <LinkOut href={null} size="xs">
            No tracking number
          </LinkOut>
        </div>
      </div>
    </Section>
  );
}

function MarkdownNotesSection() {
  const [value, setValue] = useState(
    "**Assumptions**\n\n- 2-year term\n- Volume discount approved by Stan\n\n| Item | Qty | Price |\n| --- | --- | --- |\n| Records | 5,000 | $0.02 |",
  );
  return (
    <Section
      title="MarkdownNotes"
      description="Controlled markdown editor with an Edit/Preview toggle and a formatting toolbar (B / I / U / lists / checkbox + Ctrl shortcuts). Renders GFM markdown incl. tables via MarkdownView. Presentation only — the caller owns the value and persistence. Used by CRO Forecast (debounced auto-save) and Proposals (saved with the proposal). Pass readOnly for a static preview."
    >
      <div className="grid max-w-3xl gap-4 md:grid-cols-2">
        <MarkdownNotes
          value={value}
          onChange={setValue}
          label="Editable"
          placeholder="How was this constructed…"
          minHeightPx={120}
        />
        <MarkdownNotes value={value} onChange={() => {}} label="Read-only" readOnly minHeightPx={120} />
      </div>
    </Section>
  );
}

function RichTextEditorSection() {
  const [value, setValue] = useState(
    "<h2>Rollout plan</h2><p>Ship in three phases:</p><ul><li>Phase A — schema + seed</li><li>Phase B — write-back</li></ul><ol><li>Draft</li><li>Review</li></ol><blockquote>Owner sign-off required before Phase C.</blockquote>",
  );
  return (
    <Section
      title="RichTextEditor"
      description="TipTap-based WYSIWYG editor emitting HTML. Toolbar covers bold / italic / strike, H2 / H3, bullet + numbered lists, blockquote, code block, and link (inline URL prompt). Render the saved HTML inside a `.rich-text` container so the read-only view matches the editor. Used for objective plan notes and What's New entry bodies."
    >
      <div className="grid max-w-3xl gap-4 md:grid-cols-2">
        <div>
          <SubLabel>Editable</SubLabel>
          <RichTextEditor value={value} onChange={setValue} placeholder="Outline the plan…" />
        </div>
        <div>
          <SubLabel>Rendered (.rich-text)</SubLabel>
          <div
            className="rich-text min-h-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            dangerouslySetInnerHTML={{ __html: value }}
          />
        </div>
      </div>
    </Section>
  );
}

function SortableListSection() {
  const [rows, setRows] = useState([
    { id: "kickoff", label: "Kick off discovery calls" },
    { id: "pilot", label: "Run the pilot cohort" },
    { id: "rollout", label: "Roll out to all accounts" },
    { id: "review", label: "Post-launch review" },
  ]);
  return (
    <Section
      title="SortableList"
      description="Drag-to-reorder vertical list (dnd-kit). Rows are direct children of the container so divide-y/gap styling keeps working; place the render-prop `handle` wherever the grip should sit. Keyboard accessible: focus a handle, Space to lift, arrows to move, Space to drop. `disabled` renders the rows without handles. Caller persists via onReorder(orderedIds) — used by objective milestones."
    >
      <div className="max-w-md">
        <SortableList
          items={rows}
          getId={(r) => r.id}
          onReorder={(ids) =>
            setRows((prev) => ids.flatMap((id) => prev.find((r) => r.id === id) ?? []))
          }
          className="flex flex-col divide-y divide-border rounded-md border border-border"
          renderItem={(row, handle) => (
            <div className="flex items-center gap-2 px-3 py-2 text-sm">
              {handle}
              {row.label}
            </div>
          )}
        />
      </div>
    </Section>
  );
}

function KpiTileSection() {
  const [attachFilter, setAttachFilter] = useState(false);
  return (
    <Section
      title="KpiTile"
      description="The reusable stat card: label, big number, optional sub-line, tone, and info popover. New for Upsell Ops: `onClick` + `selected` turn a tile into a filter toggle (hover lift, Enter/Space activate), `progress` adds a bottom fill bar (e.g. attach rate), and `swatchClass` ties the tile to a same-colored series/column elsewhere on the page."
    >
      <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiTile label="Active customers" value="196" sub="latest complete month" />
        <KpiTile label="NRR" value="104%" tone="positive" sub="rolling 12 months" />
        <KpiTile
          label="Reputation"
          value={
            <>
              67%
              <span className="ml-1.5 text-sm font-semibold text-muted-foreground">
                attached
              </span>
            </>
          }
          sub="131/196 customers · 65 whitespace — click to filter"
          swatchClass="bg-purple"
          progress={{ pct: 67, barClass: "bg-purple" }}
          onClick={() => setAttachFilter((v) => !v)}
          selected={attachFilter}
        />
      </div>
    </Section>
  );
}

function ProductStatePillSection() {
  return (
    <Section
      title="ProductStatePill"
      description="Product ownership cell for whitespace/attach tables (Upsell Ops). Three states — billed (solid $, on this month's invoice), contracted (outlined ✓, on an active contract but not billing), none (dashed +, open whitespace). `color` takes a DS palette token; each pill carries an explanatory tooltip."
    >
      <div className="flex flex-wrap items-center gap-4">
        {(["brand", "power", "purple", "success", "orange", "teal"] as const).map((c) => (
          <div key={c} className="flex items-center gap-1.5">
            <ProductStatePill state="billed" color={c} />
            <ProductStatePill state="contracted" color={c} />
            <span className="text-xs text-muted-foreground">{c}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <ProductStatePill state="none" />
          <span className="text-xs text-muted-foreground">whitespace</span>
        </div>
      </div>
    </Section>
  );
}

function SectionDividerSection() {
  return (
    <Section
      title="SectionDivider"
      description="Taller filled bar for heading a section — the tone colour blooms upward from the bottom edge with the label set inside. accent (brand cyan) for primary headers, muted (grey, default) for low-key breaks. Used on the CRO forecast Summary tab to split Month vs Quarter and on the KPI dashboards to head each section."
    >
      <div className="space-y-6">
        <SectionDivider label="March 2026" description="Month-to-date" />
        <SectionDivider label="Q1 2026" description="Quarter-to-date" tone="accent" />
        <SectionDivider label="Left-aligned" align="left" />
        <SectionDivider label="Right-aligned" align="right" />
      </div>
    </Section>
  );
}

function ReviewVerdictSection() {
  const [saved, setSaved] = useState<string>("nothing saved yet");
  return (
    <Section
      title="ReviewVerdict"
      description="The approve / flag / clear panel every reconciliation worklist shares — three mutually exclusive verdicts, notes, who last touched it, and a Save that stays inert until something actually changed. It owns the draft state so a background refetch can't wipe a half-typed note, and re-seeds only when itemKey changes. Pass presetFlag to open straight into a flagged draft with the notes box focused (the row-level Flag button); deniedReason explains a disabled panel. approveLabel/flagLabel rename the verdict buttons (the Integrations Monitor reads better as Expected / Real issue) and statusLabel renames the badge WORD without changing its tone (R-16 shows an approved relationship as Verified), and `extra` renders caller-owned content — e.g. a cause Select — between the buttons and the notes; its state stays in the caller and rides the same save mutation. Used by R-08, R-16 and integrations; R-01/R-02/R-03/R-04/R-14 still carry their own copies to collapse onto this."
    >
      <div className="max-w-md space-y-2">
        <ReviewVerdict
          itemKey="demo"
          status="pending"
          notes=""
          reviewedByName="Justin Rae"
          reviewedAt={new Date().toISOString()}
          canAct
          flagPlaceholder="What's wrong? (context for whoever fixes it)"
          onSave={({ status, notes }) =>
            setSaved(`saved: ${status}${notes ? ` — "${notes}"` : ""}`)
          }
        />
        <ReviewVerdict
          itemKey="demo-labels"
          status="approved"
          statusLabel="Verified"
          notes=""
          canAct
          approveLabel="Expected"
          flagLabel="Real issue"
          extra={
            <p className="text-xs text-muted-foreground">
              (caller-owned `extra` slot — integrations puts its cause picker here)
            </p>
          }
          flagPlaceholder="What happened? This feeds detection and future ML."
          onSave={({ status, notes }) =>
            setSaved(`saved: ${status}${notes ? ` — "${notes}"` : ""}`)
          }
        />
        <p className="text-xs text-muted-foreground">{saved}</p>
      </div>
    </Section>
  );
}

function EntityChainSection() {
  const [clicked, setClicked] = useState<string | null>(null);
  return (
    <Section
      title="EntityChain"
      description="Breadcrumb of an entity's ancestry with each link tagged by what KIND of thing it is. Built for 'where do I actually look?' — a deepest-name-only row is ambiguous, since a store name means nothing until you know whose company owns it and which feed carries it. The last link is the subject and reads solid; ancestors are muted, and become clickable drill targets when onLinkClick is passed. Used by the Integrations Monitor incident and entity drawers."
    >
      <div className="flex flex-col gap-4">
        <div>
          <SubLabel>Location — full chain, ancestors navigable</SubLabel>
          <EntityChain
            links={[
              { id: "jiffylube_sftp", label: "Integration", name: "jiffylube_sftp" },
              { id: "c-1", label: "Company", name: "Multi-Management Group dba Jiffy Lube" },
              { id: "l-1", label: "Location", name: "JIFFY LUBE # 1307", current: true },
            ]}
            onLinkClick={(l) => setClicked(l.name ?? l.id)}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Click an ancestor{clicked ? ` — drilled to: ${clicked}` : ""}.
          </p>
        </div>
        <div>
          <SubLabel>Company — chain stops at the subject</SubLabel>
          <EntityChain
            links={[
              { id: "meraki_grow", label: "Integration", name: "meraki_grow" },
              { id: "c-2", label: "Company", name: "Jiffy Lube Arizona", current: true },
            ]}
          />
        </div>
        <div>
          <SubLabel>Unresolved name — falls back to the id, chain still reads</SubLabel>
          <EntityChain
            links={[
              { id: "isi", label: "Integration", name: "isi" },
              { id: "f8cc5fea-9e5f-43b1-9a38-c34dde500eba", label: "Company" },
              { id: "51ab5618-7450-4562-93ef-21ed0e775001", label: "Location",
                name: "Greenwell Springs Road", current: true },
            ]}
          />
        </div>
      </div>
    </Section>
  );
}

function EntityNameSection() {
  return (
    <Section
      title="EntityName"
      description="Two-line identity cell for tables keyed on an opaque id: readable name on top, muted context beneath. fallback is the whole point — names come from a dimension read that can legitimately return nothing (brand-new entity, deleted row, warehouse down), so pass the raw id and the row degrades to it instead of rendering blank. badge takes a chip (StatusBadge, usually) inline after the name. Used by the Integrations Monitor watch list, its company/location tables, and the entity drawer's child walk."
    >
      <div className="flex flex-col gap-4">
        <div>
          <SubLabel>Resolved — name found, context is the parent trail</SubLabel>
          <EntityName
            name="Acme Auto Care — Tempe"
            fallback="9a2b4c17-3f8e-4d21-b6c5-1e0847af9d33"
            context="grow · Acme Automotive Group"
          />
        </div>
        <div>
          <SubLabel>With a badge — a chip that qualifies the name itself</SubLabel>
          <EntityName
            name="Acme Auto Care — Mesa"
            fallback="4d71e8a2-90bc-4f13-8e77-2ab5c9014f6e"
            context="grow · Acme Automotive Group · ref STORE-4471"
            badge={<StatusBadge value="inactive" label="Inactive in Cinch" />}
          />
        </div>
        <div>
          <SubLabel>Unresolved — falls back to the id, still readable as a row</SubLabel>
          <EntityName
            name={null}
            fallback="tekmetric/8f3c1e2a-77d4-4b09-9c31-5ea6d0b21847"
            context="tekmetric"
          />
        </div>
      </div>
    </Section>
  );
}

function FlowGraphSection() {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <Section
      title="FlowGraph"
      description="Layered flow graph for A-feeds-B-feeds-C relationships (COS-Units visual map: vendor → invoice charge → product line). Edge width scales with value; dashed = apportioned downstream; muted = excluded. Hover or click a node to highlight its neighborhood."
    >
      <FlowGraph
        columns={[
          { key: "vendor", title: "Vendor" },
          { key: "charge", title: "Charge" },
          { key: "line", title: "Product line" },
        ]}
        nodes={[
          { id: "v:twilio", column: "vendor", label: "Twilio", value: 58487 },
          { id: "v:el", column: "vendor", label: "Eleven Labs", value: 2794 },
          { id: "c:sms", column: "charge", label: "Programmable Messaging", value: 46705 },
          { id: "c:support", column: "charge", label: "Support + taxes", value: 6340 },
          { id: "c:plan", column: "charge", label: "Enterprise plan", value: 1294 },
          { id: "c:success", column: "charge", label: "Success package", value: 1500 },
          { id: "l:text", column: "line", label: "Text Messaging", value: 53473 },
          { id: "l:voice", column: "line", label: "Voice", value: 1336 },
          { id: "l:excluded", column: "line", label: "Excluded from COGS", value: 1500, tone: "muted" },
        ]}
        links={[
          { source: "v:twilio", target: "c:sms", value: 46705 },
          { source: "v:twilio", target: "c:support", value: 6340 },
          { source: "v:el", target: "c:plan", value: 1294 },
          { source: "v:el", target: "c:success", value: 1500 },
          { source: "c:sms", target: "l:text", value: 46705 },
          { source: "c:support", target: "l:text", value: 6340, dashed: true },
          { source: "c:support", target: "l:voice", value: 640, dashed: true },
          { source: "c:plan", target: "l:voice", value: 1294 },
          { source: "c:success", target: "l:excluded", value: 1500, muted: true },
        ]}
        formatValue={(v) => `$${Math.round(v).toLocaleString()}`}
        selectedId={selected}
        onNodeClick={(n) => setSelected(n.id === selected ? null : n.id)}
      />
    </Section>
  );
}

function RelationshipTreeSection() {
  const [selected, setSelected] = useState<string | null>(null);
  const root: RelationshipTreeNode = {
    id: "acme",
    name: "Acme Automotive Group",
    meta: "Account: Acme Group · Phoenix, AZ",
    badges: <StatusBadge value="parent" />,
    children: [
      {
        id: "acme-west",
        name: "Acme West",
        meta: "Tucson, AZ",
        badges: <StatusBadge value="parent" label="Parent · Child" />,
        children: [
          { id: "acme-west-1", name: "Acme Tucson North", meta: "Tucson, AZ" },
          {
            id: "acme-west-2",
            name: "Acme Tucson South",
            meta: "Tucson, AZ",
            muted: true,
            badges: <StatusBadge value="inactive" />,
          },
        ],
      },
      { id: "acme-east", name: "Acme East", meta: "Albuquerque, NM" },
      {
        id: "odd-one",
        name: "Bob's Tire Barn",
        meta: "Portland, OR",
        badges: <StatusBadge value="flagged" />,
      },
    ],
  };
  return (
    <Section
      title="RelationshipTree"
      description="Vertical entity tree for parent/child relationships — the root reads as the brand-tinted anchor, children hang off connector rails, and stacked relationships nest arbitrarily deep. badges is a free chip slot (StatusBadge here); muted greys inactive nodes; pass onNodeClick + selectedId to make it navigable. Used by Parent Companies' expanded view."
    >
      <div className="max-w-xl">
        <RelationshipTree
          root={root}
          selectedId={selected}
          onNodeClick={(n) => setSelected(n.id)}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Click a node to select it{selected ? ` — selected: ${selected}` : ""}.
        </p>
      </div>
    </Section>
  );
}

// ─── helpers ─────────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <Card>
        <CardContent className="p-6">{children}</CardContent>
      </Card>
    </section>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">{children}</div>
  );
}

// ─── sections ─────────────────────────────────────────────────────

function BrandSection() {
  return (
    <Section
      title="Logo"
      description="Never render the wordmark or wave in black — only brand cyan or white."
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <SubLabel>Wordmark — light backgrounds</SubLabel>
          <div className="flex items-center gap-6 rounded-md border bg-background p-6">
            <Logo variant="wordmark" tone="brand" className="h-6" />
            <Logo variant="wordmark" tone="brand" className="h-10" />
          </div>
        </div>
        <div className="space-y-3">
          <SubLabel>Wordmark — dark backgrounds</SubLabel>
          <div className="flex items-center gap-6 rounded-md bg-foreground p-6">
            <Logo variant="wordmark" tone="white" className="h-6" />
            <Logo variant="wordmark" tone="white" className="h-10" />
          </div>
        </div>
        <div className="space-y-3">
          <SubLabel>Wave mark — full color (gradient)</SubLabel>
          <div className="flex items-center gap-6 rounded-md border bg-background p-6">
            <Logo variant="mark" className="h-8 w-8" />
            <Logo variant="mark" className="h-12 w-12" />
            <Logo variant="mark" className="h-20 w-20" />
          </div>
        </div>
        <div className="space-y-3">
          <SubLabel>Wave mark — monochrome</SubLabel>
          <div className="flex items-center gap-6 rounded-md border bg-background p-6">
            <Logo variant="mark-mono" tone="brand" className="h-8 w-8" />
            <Logo variant="mark-mono" tone="brand" className="h-12 w-12" />
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-foreground">
              <Logo variant="mark-mono" tone="white" className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ColorTokensSection() {
  return (
    <Section
      title="Color tokens"
      description="Edit values in src/index.css. Never hard-code hex in .tsx."
    >
      <div className="space-y-6">
        <div>
          <SubLabel>Identity</SubLabel>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Swatch name="brand" cls="bg-brand" purpose="Logo, links, focus rings, friendly identity" />
            <Swatch name="brand-light" cls="bg-brand-light" purpose="Tinted backgrounds" />
            <Swatch name="brand-hover" cls="bg-brand-hover" purpose="Hover" />
            <Swatch name="power" cls="bg-power" purpose="CTAs, active state, hero" />
            <Swatch name="power-light" cls="bg-power-light" purpose="Active row + sidebar fill" />
            <Swatch name="power-hover" cls="bg-power-hover" purpose="Hover" />
          </div>
        </div>
        <Separator />
        <div>
          <SubLabel>Status — full</SubLabel>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Swatch name="success" cls="bg-success" purpose="Active / healthy" />
            <Swatch name="warning" cls="bg-warning" purpose="At risk / pending" />
            <Swatch name="danger" cls="bg-danger" purpose="Churned / overdue" />
            <Swatch name="info" cls="bg-info" purpose="Informational" />
            <Swatch name="neutral" cls="bg-neutral" purpose="Inactive / archived" />
          </div>
        </div>
        <div>
          <SubLabel>Status — light (opaque chip fills, won't blend over selected rows)</SubLabel>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Swatch name="success-light" cls="bg-success-light" purpose="Light chip fill" />
            <Swatch name="warning-light" cls="bg-warning-light" purpose="Light chip fill" />
            <Swatch name="danger-light" cls="bg-danger-light" purpose="Light chip fill" />
            <Swatch name="info-light" cls="bg-info-light" purpose="Light chip fill" />
            <Swatch name="neutral-light" cls="bg-neutral-light" purpose="Light chip fill" />
          </div>
        </div>
        <Separator />
        <div>
          <SubLabel>Surfaces</SubLabel>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Swatch name="background" cls="bg-background border" purpose="Page" />
            <Swatch name="card" cls="bg-card border" purpose="Cards" />
            <Swatch name="muted" cls="bg-muted" purpose="Subtle fills" />
            <Swatch name="accent" cls="bg-accent" purpose="Hover state for items" />
          </div>
        </div>
        <Separator />
        <div>
          <SubLabel>Extended palette — reports, dashboards, owner accents</SubLabel>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Swatch name="brand-deep" cls="bg-brand-deep" purpose="Saturated brand CTA" />
            <Swatch name="brand-xlight" cls="bg-brand-xlight border" purpose="Brand wash bg" />
            <Swatch name="power-deep" cls="bg-power-deep" purpose="Decisive power CTA" />
            <Swatch name="power-xlight" cls="bg-power-xlight border" purpose="Power wash bg" />
            <Swatch name="purple" cls="bg-purple" purpose="Status / category" />
            <Swatch name="purple-light" cls="bg-purple-light" purpose="Light chip fill" />
            <Swatch name="teal" cls="bg-teal" purpose="Status / category" />
            <Swatch name="teal-light" cls="bg-teal-light" purpose="Light chip fill" />
            <Swatch name="orange" cls="bg-orange" purpose="Status / category" />
            <Swatch name="orange-light" cls="bg-orange-light" purpose="Light chip fill" />
            <Swatch name="foreground-subtle" cls="bg-foreground-subtle" purpose="Quiet text" />
            <Swatch name="foreground-strong" cls="bg-foreground-strong" purpose="Heading text" />
            <Swatch name="surface-raised" cls="bg-surface-raised border" purpose="Raised surface" />
            <Swatch name="surface-overlay" cls="bg-surface-overlay" purpose="Overlay / scrim" />
            <Swatch name="border-strong" cls="bg-border-strong" purpose="Emphasized border" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function Swatch({ name, cls, purpose }: { name: string; cls: string; purpose: string }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <div className={`h-14 ${cls}`} />
      <div className="px-3 py-2">
        <div className="font-mono text-xs">{name}</div>
        <div className="text-[11px] text-muted-foreground">{purpose}</div>
      </div>
    </div>
  );
}

function TypographySection() {
  return (
    <Section title="Typography" description="Inter, system fallback for the rest.">
      <div className="space-y-3">
        <div className="text-2xl font-bold tracking-tight">Heading — 2xl bold</div>
        <div className="text-xl font-semibold">Heading — xl semibold</div>
        <div className="text-base">Body — base</div>
        <div className="text-sm text-muted-foreground">Helper — sm muted</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Eyebrow — xs uppercase tracking-wider
        </div>
        <div className="font-mono text-sm">Mono — sm</div>
      </div>
    </Section>
  );
}

function ButtonsSection() {
  return (
    <Section
      title="Buttons"
      description="Default is power blue (CTAs). Outline / ghost / link stay on brand cyan for quieter actions."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm">Small</Button>
          <Button>Default size</Button>
          <Button size="lg">Large</Button>
          <Button disabled>Disabled</Button>
        </div>
      </div>
    </Section>
  );
}

function BadgesSection() {
  return (
    <Section
      title="Badges & status"
      description="Reach for <StatusBadge value=... /> for renewal / pipeline status — it maps the string to the right variant."
    >
      <div className="space-y-5">
        <div>
          <SubLabel>Foundational</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
        </div>
        <div>
          <SubLabel>Status — light (default for tables, filters, drawers)</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="danger">Danger</Badge>
            <Badge variant="info">Info</Badge>
            <Badge variant="neutral">Neutral</Badge>
          </div>
        </div>
        <div>
          <SubLabel>Status — solid (sparingly, when the chip itself needs to stand out)</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success-solid">Success</Badge>
            <Badge variant="warning-solid">Warning</Badge>
            <Badge variant="danger-solid">Danger</Badge>
            <Badge variant="info-solid">Info</Badge>
          </div>
        </div>
        <div>
          <SubLabel>StatusBadge — maps strings → variants</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value="active" />
            <StatusBadge value="new" />
            <StatusBadge value="at_risk" />
            <StatusBadge value="decreasing" />
            <StatusBadge value="churned" />
            <StatusBadge value="merged" />
            <StatusBadge value="closed_won" />
            <StatusBadge value="closed_lost" />
            <StatusBadge value="pending" />
            <StatusBadge value="draft" />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            <code className="rounded bg-muted px-1">new</code> is the lone solid
            status — new logos earn the emphasis. <code className="rounded bg-muted px-1">decreasing</code>{" "}
            borrows the at-risk colors for shrinking accounts.
          </p>
        </div>
        <div>
          <SubLabel>TierChip — five-step palette for revenue tiers</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <TierChip tier={1} />
            <TierChip tier={2} />
            <TierChip tier={3} />
            <TierChip tier={4} />
            <TierChip tier={5} />
            <TierChip tier={null} />
          </div>
        </div>
        <div>
          <SubLabel>RankDelta — leaderboard movement (positive = climbed)</SubLabel>
          <div className="flex flex-wrap items-center gap-5">
            <RankDelta value={3} />
            <RankDelta value={-2} />
            <RankDelta value={0} />
            <RankDelta value={null} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Pass a signed delta where positive means moved up. ▲ green up, ▼ red
            down, – held, — not ranked.
          </p>
        </div>
        <div>
          <SubLabel>DeltaCell — signed money delta (arrow = direction, color = good/bad)</SubLabel>
          <div className="flex flex-wrap items-center gap-5">
            <DeltaCell value={66_293} />
            <DeltaCell value={-200_561} />
            <DeltaCell value={0.4} />
            <DeltaCell value={null} />
            <DeltaCell value={66_293} invert />
            <DeltaCell value={-200_561} invert />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Default: up is good (revenue, ARR). Pass{" "}
            <code className="rounded bg-muted px-1">invert</code> where up is bad —
            spend vs budget, churn — the arrow keeps the number's direction, the
            color flips (last two above). Near-zero renders an em-dash.
          </p>

          <SubLabel>
            BenchmarkCell — the value itself, tinted by how it beat its benchmark
          </SubLabel>
          <div className="flex flex-wrap items-center gap-5">
            <BenchmarkCell value={8_318} benchmark={7_999} invert />
            <BenchmarkCell value={7_439} benchmark={9_770} invert />
            <BenchmarkCell value={8_318} benchmark={7_999} />
            <BenchmarkCell value={7_439} benchmark={9_770} />
            <BenchmarkCell value={432} benchmark={9_981} invert pending />
            <BenchmarkCell value={null} benchmark={9_981} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            The sibling of DeltaCell: DeltaCell shows the{" "}
            <em>difference</em>, BenchmarkCell shows the{" "}
            <em>number</em> and lets colour carry the verdict — for dense grids
            where an arrow column would be noise. Same{" "}
            <code className="rounded bg-muted px-1">invert</code> convention
            (first two are spend, next two revenue, so identical figures flip
            colour). <code className="rounded bg-muted px-1">pending</code>{" "}
            (fifth) leaves an unfinished period untinted — six days into a month
            everything looks under budget. Hover for the over/under summary.
          </p>

          <SubLabel>BenchmarkCell — clickable, for drill-down grids</SubLabel>
          <div className="flex flex-wrap items-center gap-5">
            <BenchmarkCell
              value={8_318}
              benchmark={7_999}
              invert
              actionHint="click for GL lines"
              onClick={() => {}}
            />
            <BenchmarkCell
              value={7_439}
              benchmark={9_770}
              invert
              actionHint="click for GL lines"
              onClick={() => {}}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Pass <code className="rounded bg-muted px-1">onClick</code> and the
            cell becomes an inline button with a dotted underline;{" "}
            <code className="rounded bg-muted px-1">actionHint</code> appends to
            the tooltip so the drill-down stays discoverable.
          </p>

          <SubLabel>DeltaCell chip — same semantics as a tinted pill</SubLabel>
          <div className="flex flex-wrap items-center gap-3">
            <DeltaCell value={66_293} chip />
            <DeltaCell value={-200_561} chip />
            <DeltaCell value={66_293} chip invert />
            <DeltaCell value={null} chip />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Pass <code className="rounded bg-muted px-1">chip</code> inside KPI
            tiles and dense tables, where the delta needs to read as its own
            status object rather than as part of the number beside it. Stays
            light-tinted — a week-over-week move is informational, not an alarm.
            Use <code className="rounded bg-muted px-1">emptyLabel</code> to say
            why there's nothing to compare instead of a bare em-dash.
          </p>
        </div>
      </div>
    </Section>
  );
}

function FormFieldSection() {
  const [first, setFirst] = useState("Justin");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const emailErr = email && !emailValid ? "Enter a valid email address." : undefined;
  return (
    <Section
      title="Form fields"
      description="<Field> floats the label up + colors the border by tone: neutral → red (error) → green (valid)."
    >
      <div className="grid max-w-xl gap-4">
        <Field
          id="first"
          label="First name"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          required
          valid={first.length > 0}
        />
        <Field
          id="email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailErr}
          valid={emailValid}
          helper="We'll never share this."
        />
        <Field
          id="phone"
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={!phone ? "Phone is required." : undefined}
          valid={phone.length >= 10}
          required
        />
        <div className="grid gap-1.5">
          <SubLabel>Textarea — multi-line free text</SubLabel>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notes, context, longer free text…"
            aria-label="Notes"
          />
        </div>
      </div>
    </Section>
  );
}

function TooltipSection() {
  const [unlimited, setUnlimited] = useState(true);
  return (
    <Section
      title="Tooltip"
      description="A hover / focus note for terse UI — an icon-only control, a truncated value, or a rule that needs one line to explain. Portals to the body so it escapes sticky stacking contexts. Wrap a subtree in TooltipProvider once (set delayDuration there), and use asChild on the trigger so the tooltip borrows your own element. Two tones via the tone prop: default (neutral dark) and info (solid info — same token as the info-solid status badge) for a hint rather than a warning. Keep it to a short phrase — reach for InfoPopover or a Dialog when the content is longer or interactive."
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-wrap items-center gap-8">
          {/* Icon-only button — default (neutral dark) tone */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More info">
                <Info className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Default tone — icon-only actions should always carry a tooltip.</TooltipContent>
          </Tooltip>

          {/* Checkbox + icon (the Proposals "Unlimited" pattern) — info tone */}
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="flex cursor-pointer items-center gap-1 text-muted-foreground transition-colors hover:text-foreground">
                <Checkbox checked={unlimited} onCheckedChange={(v) => setUnlimited(v === true)} />
                <InfinityIcon className="h-4 w-4" />
              </label>
            </TooltipTrigger>
            <TooltipContent tone="info">Customer display as unlimited</TooltipContent>
          </Tooltip>

          {/* Dotted-underline term — info tone, longer note */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-sm underline decoration-dotted underline-offset-4">
                Blended margin
              </span>
            </TooltipTrigger>
            <TooltipContent tone="info" side="bottom" className="max-w-xs">
              Weighted by cost across all lines — below 65% it starts costing commission.
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </Section>
  );
}

function ControlsSection() {
  const [chk1, setChk1] = useState(true);
  const [chk2, setChk2] = useState(false);
  const [swt, setSwt] = useState(true);
  return (
    <Section title="Controls">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <SubLabel>Checkbox</SubLabel>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={chk1} onCheckedChange={(v) => setChk1(v === true)} />
            <span>Subscribe to product updates</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={chk2} onCheckedChange={(v) => setChk2(v === true)} />
            <span>Email me about beta features</span>
          </label>
        </div>
        <div className="space-y-3">
          <SubLabel>Switch</SubLabel>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={swt} onCheckedChange={setSwt} />
            <span>{swt ? "Enabled" : "Disabled"}</span>
          </label>
          <p className="text-xs text-muted-foreground">
            <strong className="font-semibold">Brand cyan ON / neutral gray OFF.</strong> The
            red-on / green-off pattern has been retired across the industry — red/green is the
            most common color-blindness pair, and "off = red = bad" carries the wrong tone for
            a benign toggle. State is conveyed by the thumb position; color reinforces it.
          </p>
        </div>
        <div className="space-y-3 md:col-span-2">
          <SubLabel>Progress</SubLabel>
          <Progress value={72} />
          <div className="text-xs text-muted-foreground">72% complete</div>
        </div>
      </div>
    </Section>
  );
}

function TabsSection() {
  return (
    <Section
      title="Tabs"
      description="Two variants. Underline (with count badges) is the canonical top-of-page section nav; segmented is for inline filters/toggles."
    >
      <div className="space-y-6">
        <div>
          <SubLabel>Underline — section nav with counts</SubLabel>
          <Tabs defaultValue="orphan-contracts">
            <TabsList variant="underline">
              <TabsTrigger value="orphan-contracts" count={31}>
                Orphan contracts
              </TabsTrigger>
              <TabsTrigger value="orphan-accounts" count={91}>
                Orphan accounts
              </TabsTrigger>
              <TabsTrigger value="po-no-master" count={5}>
                POs w/o master
              </TabsTrigger>
              <TabsTrigger value="po-no-company" count={29}>
                POs w/o matching company
              </TabsTrigger>
            </TabsList>
            <TabsContent value="orphan-contracts" className="text-sm text-muted-foreground">
              Tab content for orphan contracts.
            </TabsContent>
            <TabsContent value="orphan-accounts" className="text-sm text-muted-foreground">
              Tab content for orphan accounts.
            </TabsContent>
            <TabsContent value="po-no-master" className="text-sm text-muted-foreground">
              Tab content for POs without master.
            </TabsContent>
            <TabsContent value="po-no-company" className="text-sm text-muted-foreground">
              Tab content for POs without matching company.
            </TabsContent>
          </Tabs>
        </div>
        <div>
          <SubLabel>Segmented — inline toggle</SubLabel>
          <Tabs defaultValue="a">
            <TabsList>
              <TabsTrigger value="a">Overview</TabsTrigger>
              <TabsTrigger value="b">Detail</TabsTrigger>
              <TabsTrigger value="c">History</TabsTrigger>
            </TabsList>
            <TabsContent value="a" className="text-sm text-muted-foreground">
              Overview content.
            </TabsContent>
            <TabsContent value="b" className="text-sm text-muted-foreground">
              Detail content.
            </TabsContent>
            <TabsContent value="c" className="text-sm text-muted-foreground">
              History content.
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Section>
  );
}

interface TableRowFix {
  id: string;
  name: string;
  mrr: number;
  status: string;
  renewal: string;
}

function TableSection() {
  const rows: TableRowFix[] = useMemo(
    () => [
      { id: "acme", name: "Acme Corporation", mrr: 12500, status: "active", renewal: "2026-09-01" },
      { id: "globex", name: "Globex Industries", mrr: 8200, status: "at_risk", renewal: "2026-06-15" },
      { id: "initech", name: "Initech", mrr: 5400, status: "churned", renewal: "2026-04-30" },
      { id: "wayland", name: "Wayland-Yutani", mrr: 21000, status: "pending", renewal: "2026-12-01" },
      { id: "soylent", name: "Soylent Corp", mrr: 3100, status: "active", renewal: "2027-01-20" },
    ],
    [],
  );
  const [sel, setSel] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: keyof TableRowFix; dir: "asc" | "desc" } | null>({
    key: "mrr",
    dir: "desc",
  });

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
      const as = String(av);
      const bs = String(bv);
      return sort.dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [rows, sort]);

  function toggleSort(key: keyof TableRowFix) {
    setSort((cur) => {
      if (cur?.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }
  const sortDir = (key: keyof TableRowFix) => (sort?.key === key ? sort.dir : null);

  const mrrTotal = sorted.reduce((acc, r) => acc + r.mrr, 0);

  return (
    <Section
      title="Data table"
      description="Click any column header to sort. Selected row picks up bg-power-light; chips on the selected row stay opaque (no muddy blend). Zebra is on by default; pass zebra={false} to suppress. Wrap a totals row in <TableFooter> for a semantic <tfoot> with the standard footer chrome."
    >
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead sortable sortDirection={sortDir("name")} onSort={() => toggleSort("name")}>
                Customer
              </TableHead>
              <TableHead
                align="right"
                sortable
                sortDirection={sortDir("mrr")}
                onSort={() => toggleSort("mrr")}
              >
                MRR
              </TableHead>
              <TableHead
                sortable
                sortDirection={sortDir("status")}
                onSort={() => toggleSort("status")}
              >
                Status
              </TableHead>
              <TableHead
                sortable
                sortDirection={sortDir("renewal")}
                onSort={() => toggleSort("renewal")}
              >
                Renewal
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody zebra>
            {sorted.map((r) => (
              <TableRow
                key={r.id}
                clickable
                selected={sel === r.id}
                onClick={() => setSel(sel === r.id ? null : r.id)}
              >
                <TableCell>{r.name}</TableCell>
                <TableCell numeric>${r.mrr.toLocaleString()}</TableCell>
                <TableCell>
                  <StatusBadge value={r.status} />
                </TableCell>
                <TableCell>{r.renewal}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold uppercase tracking-wider text-xs">
                Team total
              </TableCell>
              <TableCell numeric className="font-semibold">
                ${mrrTotal.toLocaleString()}
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </Section>
  );
}

function MatrixSection() {
  // A 5×5 tier-transition grid: diagonal = held tier (brand), below = upgrade
  // (green), above = downgrade (red), empty = no accounts (muted). Tones carry
  // the meaning; the count carries the magnitude.
  const sample: Record<string, number> = {
    "1_1": 7,
    "2_2": 12,
    "3_3": 31,
    "3_4": 1,
    "4_4": 46,
    "5_3": 1,
    "5_4": 2,
    "5_5": 97,
  };
  const tiers = [1, 2, 3, 4, 5];
  // Populated cells are clickable + selectable — wire `onClick`/`selected` to
  // drive a filter elsewhere (the Customer Tiers movement tab does exactly this).
  const [picked, setPicked] = useState<string | null>(null);
  const cells: MatrixCell[][] = tiers.map((i) =>
    tiers.map((j) => {
      const key = `${i}_${j}`;
      const v = sample[key] ?? 0;
      const tone: MatrixCell["tone"] =
        v === 0 ? "neutral" : i === j ? "brand" : i < j ? "danger" : "success";
      return {
        value: v,
        tone,
        title: `T${i} → T${j}: ${v}`,
        selected: picked === key,
        onClick: v > 0 ? () => setPicked((cur) => (cur === key ? null : key)) : undefined,
      };
    }),
  );
  return (
    <Section
      title="Matrix"
      description="Heatmap grid for cross-tabbing two dimensions — tier→tier transitions here. Pass row/column header nodes (TierChips below) and a cells[row][col] grid. Cells are solid by design: a matrix is a data-viz heatmap, so the fill is a color scale, not a status chip. Empty cells fall back to a quiet neutral. Give a cell an onClick (+ selected) to make it an interactive filter — try clicking a populated cell."
    >
      <Matrix
        cornerLabel="from → to"
        columns={tiers.map((j) => (
          <span key={j} className="inline-flex items-center gap-1">
            <span aria-hidden>→</span>
            <TierChip tier={j} />
          </span>
        ))}
        rows={tiers.map((i) => (
          <span key={i} className="inline-flex items-center gap-1">
            <TierChip tier={i} />
            <span aria-hidden>→</span>
          </span>
        ))}
        cells={cells}
      />
    </Section>
  );
}

interface DemoTableRow {
  id: string;
  name: string;
  mrr: number;
  status: string;
}

function DataTableSection() {
  const all: DemoTableRow[] = useMemo(
    () => [
      { id: "acme", name: "Acme Corporation", mrr: 12500, status: "active" },
      { id: "globex", name: "Globex Industries", mrr: 8200, status: "at_risk" },
      { id: "initech", name: "Initech", mrr: 5400, status: "churned" },
      { id: "wayland", name: "Wayland-Yutani", mrr: 21000, status: "pending" },
      { id: "soylent", name: "Soylent Corp", mrr: 3100, status: "active" },
    ],
    [],
  );
  const [sort, setSort] = useState<DataTableSort | null>({ key: "mrr", dir: "desc" });
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 3;

  const filtered = useMemo(() => {
    let rows = all;
    if (search) rows = rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
    if (sort) {
      rows = [...rows].sort((a, b) => {
        const av = a[sort.key as keyof DemoTableRow];
        const bv = b[sort.key as keyof DemoTableRow];
        if (typeof av === "number" && typeof bv === "number") {
          return sort.dir === "asc" ? av - bv : bv - av;
        }
        return sort.dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return rows;
  }, [all, sort, search]);

  const columns: DataTableColumn<DemoTableRow>[] = [
    { key: "name", header: "Customer", sortable: true },
    { key: "mrr", header: "MRR", numeric: true, sortable: true, render: (r) => `$${r.mrr.toLocaleString()}` },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
  ];

  // Totals row example — sums MRR across the (post-filter) result set. Pair
  // numeric columns with their roll-up here; non-numeric columns get a label
  // ("Team total") or are left blank.
  const totalMrr = filtered.reduce((acc, r) => acc + r.mrr, 0);

  return (
    <Section
      title="DataTable"
      description="Generic server-driven table: controlled sort/search/pagination, skeleton loading, per-cell custom render (e.g. lazy columns), and optional totals row via the `footer` slot. The Contracts tabs and CRO Forecast Meeting are built on this."
    >
      <DataTable
        columns={columns}
        rows={filtered.slice(offset, offset + limit)}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={(s) => {
          setSort(s);
          setOffset(0);
        }}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setOffset(0);
        }}
        searchPlaceholder="Search customers…"
        pagination={{ limit, offset, count: filtered.length }}
        onPageChange={setOffset}
        containerClassName=""
        footer={(cols) => (
          <TableRow>
            {cols.map((c) => (
              <TableCell
                key={c.key}
                numeric={c.numeric}
                align={c.align}
                className="font-semibold"
              >
                {c.key === "name"
                  ? "Team total"
                  : c.key === "mrr"
                  ? `$${totalMrr.toLocaleString()}`
                  : ""}
              </TableCell>
            ))}
          </TableRow>
        )}
      />
    </Section>
  );
}

function PaginationSection() {
  const [offset, setOffset] = useState(0);
  return (
    <Section
      title="Pagination"
      description="Server-side pager — shows the current range and disables at the bounds. Pair with DataTable (or use standalone)."
    >
      <Pagination limit={20} offset={offset} count={504} onPageChange={setOffset} />
    </Section>
  );
}

function FilterBarSection() {
  const [q, setQ] = useState("");
  const [statuses, setStatuses] = useState<string[]>(["active", "at_risk"]);
  const [owner, setOwner] = useState<string | null>("jrae@cinch.io");
  const [renewalDate, setRenewalDate] = useState<Date | null>(null);
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [mrrMin, setMrrMin] = useState("");
  const [mrrMax, setMrrMax] = useState("");

  const toggleStatus = (s: string) =>
    setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const hasAny =
    q.length > 0 ||
    statuses.length > 0 ||
    owner != null ||
    renewalDate != null ||
    range.start != null ||
    range.end != null ||
    mrrMin !== "" ||
    mrrMax !== "";

  const clearAll = () => {
    setQ("");
    setStatuses([]);
    setOwner(null);
    setRenewalDate(null);
    setRange({ start: null, end: null });
    setMrrMin("");
    setMrrMax("");
  };

  const fmtDate = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : "");
  const rangeLabel =
    range.start || range.end
      ? `${fmtDate(range.start) || "…"} → ${fmtDate(range.end) || "…"}`
      : undefined;
  const mrrLabel =
    mrrMin || mrrMax ? `${mrrMin || "0"}–${mrrMax || "∞"}` : undefined;

  return (
    <Section
      title="Filter bar"
      description="Every common filter shape — search, multi-select, single-select, date, date range, numeric range — drops into the same FilterBar. The composed MultiSelectChip and DateRangeChip (promoted from contracts, 2026-08-19) live in the barrel too: import { MultiSelectChip, DateRangeChip } from '@/components/cinch/FilterBar' — used by contracts, proposals, and integrations."
    >
      <div className="space-y-3">
        <FilterBar>
          <FilterSearch value={q} onChange={setQ} placeholder="Search customers" />

          <FilterChip label="Status" count={statuses.length}>
            <div className="space-y-2">
              {["active", "at_risk", "churned", "pending"].map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={statuses.includes(s)}
                    onCheckedChange={() => toggleStatus(s)}
                  />
                  <StatusBadge value={s} />
                </label>
              ))}
            </div>
          </FilterChip>

          <FilterChip label="Owner" valueLabel={owner ?? undefined}>
            <div className="space-y-1 text-sm">
              {[
                "jrae@cinch.io",
                "alex@cinch.io",
                "samira@cinch.io",
                "kai@cinch.io",
              ].map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOwner(owner === o ? null : o)}
                  className="block w-full rounded px-2 py-1 text-left hover:bg-accent data-[selected=true]:bg-brand-light data-[selected=true]:text-brand"
                  data-selected={owner === o}
                >
                  {o}
                </button>
              ))}
            </div>
          </FilterChip>

          <FilterChip
            label="Renewal date"
            valueLabel={renewalDate ? fmtDate(renewalDate) : undefined}
          >
            <div className="space-y-2">
              <DatePicker
                value={renewalDate}
                onChange={setRenewalDate}
                placeholder="Pick a date"
              />
              {renewalDate ? (
                <button
                  type="button"
                  onClick={() => setRenewalDate(null)}
                  className="text-xs text-brand hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </FilterChip>

          <FilterChip label="Renewal range" valueLabel={rangeLabel}>
            <div className="space-y-2">
              <DateRangePicker value={range} onChange={setRange} placeholder="Pick a range" />
              {range.start || range.end ? (
                <button
                  type="button"
                  onClick={() => setRange({ start: null, end: null })}
                  className="text-xs text-brand hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </FilterChip>

          <FilterChip label="MRR" valueLabel={mrrLabel}>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Field
                  id="mrr-min"
                  label="Min"
                  type="number"
                  value={mrrMin}
                  onChange={(e) => setMrrMin(e.target.value)}
                />
                <span className="text-muted-foreground">–</span>
                <Field
                  id="mrr-max"
                  label="Max"
                  type="number"
                  value={mrrMax}
                  onChange={(e) => setMrrMax(e.target.value)}
                />
              </div>
              {mrrMin || mrrMax ? (
                <button
                  type="button"
                  onClick={() => {
                    setMrrMin("");
                    setMrrMax("");
                  }}
                  className="text-xs text-brand hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </FilterChip>

          <div className="flex-1" />
          {hasAny ? <FilterClearAll onClick={clearAll} /> : null}
        </FilterBar>

        {(statuses.length || owner || renewalDate || range.start || range.end || mrrMin || mrrMax) ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {statuses.map((s) => (
              <ActiveFilterPill key={`s-${s}`} label={`Status: ${s}`} onRemove={() => toggleStatus(s)} />
            ))}
            {owner ? (
              <ActiveFilterPill label={`Owner: ${owner}`} onRemove={() => setOwner(null)} />
            ) : null}
            {renewalDate ? (
              <ActiveFilterPill
                label={`Renewal: ${fmtDate(renewalDate)}`}
                onRemove={() => setRenewalDate(null)}
              />
            ) : null}
            {range.start || range.end ? (
              <ActiveFilterPill
                label={`Range: ${rangeLabel}`}
                onRemove={() => setRange({ start: null, end: null })}
              />
            ) : null}
            {mrrMin || mrrMax ? (
              <ActiveFilterPill
                label={`MRR: ${mrrLabel}`}
                onRemove={() => {
                  setMrrMin("");
                  setMrrMax("");
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}

function FeedbackSection() {
  return (
    <Section
      title="Feedback"
      description="On-page Alerts for persistent messages; Sonner toasts for transient confirmations."
    >
      <div className="space-y-6">
        <div>
          <SubLabel>On-page Alerts — tinted (default)</SubLabel>
          <div className="grid gap-3 md:grid-cols-2">
            <Alert variant="info">
              <AlertTitle>Heads up</AlertTitle>
              <AlertDescription>This is an informational message.</AlertDescription>
            </Alert>
            <Alert variant="success">
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>Your changes were saved.</AlertDescription>
            </Alert>
            <Alert variant="warning">
              <AlertTitle>Attention needed</AlertTitle>
              <AlertDescription>Renewal in 12 days — confirm forecast.</AlertDescription>
            </Alert>
            <Alert variant="danger">
              <AlertTitle>Sync failed</AlertTitle>
              <AlertDescription>
                Couldn't reach QuickBooks. Try again or contact ops.
              </AlertDescription>
            </Alert>
          </div>
        </div>
        <div>
          <SubLabel>On-page Alerts — solid (urgent / direct callouts; use sparingly)</SubLabel>
          <div className="grid gap-3 md:grid-cols-2">
            <Alert variant="info-solid">
              <AlertTitle>Maintenance window</AlertTitle>
              <AlertDescription>Snowflake reads paused 02:00–02:15 UTC tonight.</AlertDescription>
            </Alert>
            <Alert variant="success-solid">
              <AlertTitle>Migration complete</AlertTitle>
              <AlertDescription>All 247 contracts moved to the new schema.</AlertDescription>
            </Alert>
            <Alert variant="warning-solid">
              <AlertTitle>Action required</AlertTitle>
              <AlertDescription>3 renewals expire this week without a forecast.</AlertDescription>
            </Alert>
            <Alert variant="danger-solid">
              <AlertTitle>Production sync halted</AlertTitle>
              <AlertDescription>Credentials rotated — re-link the Snowflake key.</AlertDescription>
            </Alert>
          </div>
        </div>
        <div>
          <SubLabel>Toast notifications — transient (auto-dismiss)</SubLabel>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => toast("Synced")}>
              toast()
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast.success("Pipeline refreshed", {
                  description: "Last sync: a moment ago",
                })
              }
            >
              toast.success
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast.info("Heads up", { description: "3 contracts marked at-risk overnight." })
              }
            >
              toast.info
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast.warning("Renewal in 12 days", { description: "Forecast still blank." })
              }
            >
              toast.warning
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast.error("Sync failed", { description: "Snowflake returned 5xx." })
              }
            >
              toast.error
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast("Maintenance window starting in 10 min", {
                  description: "Snowflake reads will pause briefly. Close this when ready.",
                  duration: Infinity,
                })
              }
            >
              toast (persistent)
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Use toasts for the result of a user action (saved, synced, failed). Use Alerts for
            page-level state that stays put until acknowledged. The persistent variant
            (<code className="rounded bg-muted px-1">duration: Infinity</code>) is for messages
            the user must acknowledge — every toast has a close X.
          </p>
        </div>
        <div>
          <SubLabel>Loading skeleton</SubLabel>
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function DrawerSection() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <Section
      title="Entity Drawer"
      description="Slide-in quick view that expands into a full-page view. Try the expand chevron."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => {
            setExpanded(false);
            setOpen(true);
          }}
        >
          Open quick view
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setExpanded(true);
            setOpen(true);
          }}
        >
          Open expanded
        </Button>
      </div>
      <EntityDrawer
        open={open}
        onOpenChange={(v) => {
          if (!v) setExpanded(false);
          setOpen(v);
        }}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onClose={() => {
          setExpanded(false);
          setOpen(false);
        }}
        title="Acme Corporation"
        subtitle="Demo customer · #DEMO-1593"
      >
        <div className="space-y-4 p-5 text-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Owner</dt>
            <dd>Justin Rae</dd>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">MRR</dt>
            <dd className="tabular-nums">$12,500</dd>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Status</dt>
            <dd>
              <StatusBadge value="at_risk" />
            </dd>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Renewal</dt>
            <dd>September 2026</dd>
          </dl>
          <Separator />
          <p className="text-muted-foreground">
            The same drawer instance grows leftward when expanded. Try the chevron in the header.
          </p>
        </div>
      </EntityDrawer>
    </Section>
  );
}
